import axios from "../helpers/axios";
import { officePaymentAccountConstants } from "./constants";

const getErrorMessage = (error, fallback = "Ошибка") => {
    return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        fallback
    );
};

const buildQuery = (params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

    return query ? `?${query}` : "";
};

const getPaymentAccountFromResponse = (data = {}) => {
    return data.paymentAccount || data.paymentAccounts || data.result || null;
};

export const clearOfficePaymentAlert = () => ({
    type: officePaymentAccountConstants.OFFICE_PAYMENT_CLEAR_ALERT,
});

export const clearOfficePaymentState = () => ({
    type: officePaymentAccountConstants.OFFICE_PAYMENT_CLEAR_STATE,
});

export const getOfficePaymentMethods = ({ officeId } = {}) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_METHODS_REQUEST,
            });

            const params = {
                officeId,
                office: officeId,
            };

            try {
                const officeRes = await axios.get(`/office/payment-methods${buildQuery(params)}`);

                if (officeRes.status === 200) {
                    const paymentMethods =
                        officeRes.data?.paymentMethods ||
                        officeRes.data?.methods ||
                        officeRes.data?.paymentList ||
                        [];

                    dispatch({
                        type: officePaymentAccountConstants.OFFICE_PAYMENT_METHODS_SUCCESS,
                        payload: {
                            paymentMethods,
                            source: "office",
                            message: officeRes.data?.message || "",
                        },
                    });

                    return {
                        ok: true,
                        paymentMethods,
                        source: "office",
                    };
                }
            } catch (officeError) {
                // Пока office/payment-methods может не существовать.
                // В этом случае падаем на общий справочник paymentList.
            }

            const res = await axios.get("/paymentList/get");

            if (res.status === 200) {
                const paymentMethods = res.data?.paymentList || [];

                dispatch({
                    type: officePaymentAccountConstants.OFFICE_PAYMENT_METHODS_SUCCESS,
                    payload: {
                        paymentMethods,
                        source: "paymentList",
                        message: res.data?.message || "",
                    },
                });

                return {
                    ok: true,
                    paymentMethods,
                    source: "paymentList",
                };
            }

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_METHODS_FAILURE,
                payload: {
                    error: res.data?.error || res.data?.message || "Ошибка загрузки способов оплаты",
                },
            });

            return {
                ok: false,
                error: res.data?.error || res.data?.message || "Ошибка загрузки способов оплаты",
            };
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка загрузки способов оплаты");

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_METHODS_FAILURE,
                payload: {
                    error: message,
                },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};

export const getOfficePaymentAccountsByOrder = ({
    orderId,
    officeId,
} = {}) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNTS_REQUEST,
            });

            const params = {
                officeId,
                office: officeId,
            };

            const res = await axios.get(
                `/office/order/payment-accounts/${orderId}${buildQuery(params)}`
            );

            if (res.status === 200) {
                dispatch({
                    type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNTS_SUCCESS,
                    payload: {
                        paymentAccounts: res.data?.paymentAccounts || [],
                        paidAmount: res.data?.paidAmount,
                        message: res.data?.message || "",
                    },
                });

                return {
                    ok: true,
                    paymentAccounts: res.data?.paymentAccounts || [],
                    paidAmount: res.data?.paidAmount,
                    message: res.data?.message || "",
                };
            }

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNTS_FAILURE,
                payload: {
                    error: res.data?.error || res.data?.message || "Ошибка получения оплат",
                },
            });

            return {
                ok: false,
                error: res.data?.error || res.data?.message || "Ошибка получения оплат",
            };
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка получения оплат");

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNTS_FAILURE,
                payload: {
                    error: message,
                },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};

export const createOfficePaymentAccount = ({
    orderId,
    officeId,
    price,
    type = "client",
    method = "cash",
    description = "",
    itemPictures = [],
} = {}) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_CREATE_REQUEST,
            });

            const formData = new FormData();

            formData.append("order", orderId);
            formData.append("orderId", orderId);
            formData.append("officeId", officeId);
            formData.append("office", officeId);
            formData.append("price", String(price));
            formData.append("type", type || "client");
            formData.append("method", method);
            formData.append("description", description || "");

            Array.from(itemPictures || []).forEach((file) => {
                formData.append("itemPictures", file);
            });

            const params = {
                officeId,
                office: officeId,
            };

            const res = await axios.post(
                `/office/order/payment-account${buildQuery(params)}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (res.status === 200 || res.status === 201) {
                const paymentAccount = getPaymentAccountFromResponse(res.data);

                dispatch({
                    type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_CREATE_SUCCESS,
                    payload: {
                        paymentAccount,
                        paidAmount: res.data?.paidAmount,
                        message: res.data?.message || "Оплата добавлена",
                    },
                });

                dispatch(getOfficePaymentAccountsByOrder({
                    orderId,
                    officeId,
                }));

                return {
                    ok: true,
                    paymentAccount,
                    paidAmount: res.data?.paidAmount,
                    message: res.data?.message || "Оплата добавлена",
                };
            }

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_CREATE_FAILURE,
                payload: {
                    error: res.data?.error || res.data?.message || "Ошибка добавления оплаты",
                },
            });

            return {
                ok: false,
                error: res.data?.error || res.data?.message || "Ошибка добавления оплаты",
            };
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка добавления оплаты");

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_CREATE_FAILURE,
                payload: {
                    error: message,
                },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};

export const deleteOfficePaymentAccount = ({
    paymentAccountId,
    orderId,
    officeId,
} = {}) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_DELETE_REQUEST,
                meta: {
                    paymentAccountId,
                },
            });

            const params = {
                officeId,
                office: officeId,
            };

            const res = await axios.delete(
                `/office/order/payment-account/${paymentAccountId}${buildQuery(params)}`
            );

            if (res.status === 200) {
                dispatch({
                    type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_DELETE_SUCCESS,
                    payload: {
                        paymentAccountId,
                        paidAmount: res.data?.paidAmount,
                        message: res.data?.message || "Оплата удалена",
                    },
                });

                dispatch(getOfficePaymentAccountsByOrder({
                    orderId,
                    officeId,
                }));

                return {
                    ok: true,
                    paymentAccountId,
                    paidAmount: res.data?.paidAmount,
                    message: res.data?.message || "Оплата удалена",
                };
            }

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_DELETE_FAILURE,
                payload: {
                    error: res.data?.error || res.data?.message || "Ошибка удаления оплаты",
                },
            });

            return {
                ok: false,
                error: res.data?.error || res.data?.message || "Ошибка удаления оплаты",
            };
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка удаления оплаты");

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_ACCOUNT_DELETE_FAILURE,
                payload: {
                    error: message,
                },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};

export const saveOfficeOrderPaymentMethod = ({
    orderId,
    officeId,
    paymentId,
    paymentOrderOnOperator = false,
} = {}) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_METHOD_SAVE_REQUEST,
            });

            const res = await axios.post("/office/order/update-delivery-payment", {
                orderId,
                officeId,
                office: officeId,
                paymentId: paymentOrderOnOperator ? "empty" : paymentId,
                paymentOrderOnOperator,
            });

            if (res.status === 200) {
                dispatch({
                    type: officePaymentAccountConstants.OFFICE_PAYMENT_METHOD_SAVE_SUCCESS,
                    payload: {
                        order: res.data?.order || res.data?.orderDetails || null,
                        message: res.data?.message || "Способ оплаты обновлён",
                    },
                });

                return {
                    ok: true,
                    order: res.data?.order || res.data?.orderDetails || null,
                    message: res.data?.message || "Способ оплаты обновлён",
                };
            }

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_METHOD_SAVE_FAILURE,
                payload: {
                    error: res.data?.error || res.data?.message || "Ошибка сохранения способа оплаты",
                },
            });

            return {
                ok: false,
                error: res.data?.error || res.data?.message || "Ошибка сохранения способа оплаты",
            };
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка сохранения способа оплаты");

            dispatch({
                type: officePaymentAccountConstants.OFFICE_PAYMENT_METHOD_SAVE_FAILURE,
                payload: {
                    error: message,
                },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};
